import { DialogComponent } from '@theme/dialog';
import { fetchHeaderSlice } from '@theme/r-header-slices';

/* ↗ .cursor/references/code-notes/r-deferred-dialog.js.md#§1 */

const PENDING = '[data-r-drawer-body-pending]';
const INTENT = ['pointerenter', 'focusin', 'touchstart'];

class DeferredDialog extends DialogComponent {
  /** @type {Promise<boolean> | null} */
  #body = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.querySelector(PENDING)) return;

    for (const trigger of document.querySelectorAll(
      `[on\\:click="#${this.id}/showDialog"], [data-r-drawer-open="${this.id}"]`
    )) {
      for (const type of INTENT) trigger.addEventListener(type, this.#prefetch, { passive: true, once: true });
    }
  }

  #prefetch = () => {
    this.ensureBody();
  };

  /**
   * Resolve once the body is in the panel — at once when it shipped inline (theme
   * editor, a form's return render), else after the header's `utility` slice has
   * been fetched and its body adopted. A failed fetch resolves false and is not
   * remembered, so the next open tries again; the shell's fallback link stands in.
   *
   * @returns {Promise<boolean>}
   */
  ensureBody() {
    if (this.#body) return this.#body;

    const slot = this.querySelector(PENDING);
    if (!(slot instanceof HTMLElement)) return (this.#body = Promise.resolve(true));

    this.#body = fetchHeaderSlice('utility').then((doc) => {
      const source = doc?.querySelector(`[data-r-drawer-body="${this.id}"]`);
      if (!(source instanceof HTMLElement) || !slot.isConnected) {
        this.#body = null;
        return false;
      }

      slot.replaceChildren(...Array.from(source.childNodes, (node) => document.adoptNode(node)));
      slot.removeAttribute('data-r-drawer-body-pending');
      this.dispatchEvent(new CustomEvent('r-drawer:body', { bubbles: true }));
      return true;
    });

    return this.#body;
  }

  /**
   * The body first, then the dialog: showModal() moves focus into the panel, and
   * a panel that fills after it opened would leave focus on nothing and announce
   * an empty region.
   *
   * @param {...any} args
   */
  async showDialog(...args) {
    await this.ensureBody();
    super.showDialog(...args);
  }
}

if (!customElements.get('r-deferred-dialog')) {
  customElements.define('r-deferred-dialog', DeferredDialog);
}
